import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Deployments,
  Me,
  type DeploymentRow,
  type DeploymentKind,
} from '../lib/api';
import { Badge, StatusBadge } from '../components/Badge';
import type { Tone } from '../lib/ticket-ui';
import { DeploymentDialog } from '../components/DeploymentDialog';

const KIND_LABEL: Record<DeploymentKind, string> = {
  BUGFIX: 'Bug fixes',
  FEATURE: 'Feature',
  INFRA: 'Infrastructure',
  CONFIG: 'Config',
  DEPENDENCY: 'Dependency',
  HOTFIX: 'Hotfix',
  ROLLBACK: 'Rollback',
  OTHER: 'Other',
};

const KIND_TONE: Record<DeploymentKind, Tone> = {
  BUGFIX: 'info',
  FEATURE: 'active',
  INFRA: 'neutral',
  CONFIG: 'neutral',
  DEPENDENCY: 'neutral',
  HOTFIX: 'warn',
  ROLLBACK: 'danger',
  OTHER: 'neutral',
};

// Bug-fix batches derive their identity from the linked tickets, so they often
// carry no explicit title — fall back to a count.
function deployTitle(d: DeploymentRow): string {
  if (d.title?.trim()) return d.title;
  const n = d.tickets.length;
  if (n > 0) return `Bug-fix batch · ${n} ticket${n === 1 ? '' : 's'}`;
  return 'Deployment';
}

/**
 * Deployment log. The browsable counterpart to the "Record deployment" dialog:
 * every production push the system knows about, newest first — bug-fix batches
 * and standalone (feature/infra/config/…) deploys alike. Admins and developers
 * can view; admins can record from here too.
 */
export function DeploymentsPage() {
  const [rows, setRows] = useState<DeploymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await Deployments.list(100);
      setRows(res.deployments);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load deployments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    Me.get()
      .then((r) => setIsAdmin(r.user.role === 'ADMIN'))
      .catch(() => undefined);
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Deployments</h1>
          <p className="text-sm text-slate-500">
            Every production push on record — newest first.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setRecordOpen(true)}
            className="px-3 py-1.5 rounded-md bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
            type="button"
          >
            Record deployment
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-6">Loading deployments…</div>
      ) : error ? (
        <div className="text-sm text-red-600 py-6">{error}</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-6">
          No deployments recorded yet.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-md divide-y divide-slate-100 bg-white">
          {rows.map((d) => (
            <div key={d.id} className="px-4 py-3 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge tone={KIND_TONE[d.kind]}>{KIND_LABEL[d.kind]}</Badge>
                    <span className="text-sm font-medium text-slate-900 truncate">
                      {deployTitle(d)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {d.deployedBy.name} · {new Date(d.createdAt).toLocaleString()}
                  </div>
                </div>
                {d.refUrl && (
                  <a
                    href={d.refUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-brand-700 hover:text-brand-800 underline shrink-0"
                  >
                    Reference ↗
                  </a>
                )}
              </div>

              {d.tickets.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {d.tickets.map(({ ticket }) => (
                    <Link
                      key={ticket.id}
                      to={`/tickets/${ticket.id}`}
                      className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50"
                      title={ticket.title}
                    >
                      <span className="text-slate-500">#{ticket.id}</span>
                      <span className="max-w-[220px] truncate text-slate-700">
                        {ticket.title}
                      </span>
                      <StatusBadge status={ticket.status} />
                    </Link>
                  ))}
                </div>
              )}

              {d.notes && (
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{d.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {recordOpen && (
        <DeploymentDialog
          onClose={() => setRecordOpen(false)}
          onSaved={() => {
            setRecordOpen(false);
            void load();
          }}
        />
      )}
    </div>
  );
}
